import * as React from 'react'
import {
  resolveValue,
  ToastBar,
  useToaster,
  type ToasterProps,
  type ToastPosition,
} from 'react-hot-toast'
import { css } from 'goober'

const prefersReducedMotion = (() => {
  let shouldReduceMotion: boolean | undefined

  return () => {
    if (shouldReduceMotion === undefined && typeof window !== 'undefined') {
      const mediaQuery = matchMedia('(prefers-reduced-motion: reduce)')
      shouldReduceMotion = !mediaQuery || mediaQuery.matches
    }

    return shouldReduceMotion
  }
})()

type ToastWrapperProps = {
  id: string
  className?: string
  style?: React.CSSProperties
  onHeightUpdate: (id: string, height: number) => void
  children?: React.ReactNode
}

const ToastWrapper = ({
  id,
  className,
  style,
  onHeightUpdate,
  children,
}: ToastWrapperProps) => {
  const ref = React.useCallback(
    (el: HTMLElement | null) => {
      if (el) {
        const updateHeight = () => {
          const height = el.getBoundingClientRect().height
          onHeightUpdate(id, height)
        }
        updateHeight()
        new MutationObserver(updateHeight).observe(el, {
          subtree: true,
          childList: true,
          characterData: true,
        })
      }
    },
    [id, onHeightUpdate]
  )

  return (
    <div ref={ref} className={className} style={style}>
      {children}
    </div>
  )
}

const getPositionStyle = (
  position: ToastPosition,
  offset: number
): React.CSSProperties => {
  const top = position.includes('top')
  const verticalStyle: React.CSSProperties = top ? { top: 0 } : { bottom: 0 }
  const horizontalStyle: React.CSSProperties = position.includes('center')
    ? { justifyContent: 'center' }
    : position.includes('right')
      ? { justifyContent: 'flex-end' }
      : {}

  return {
    left: 0,
    right: 0,
    display: 'flex',
    position: 'absolute',
    transition: prefersReducedMotion()
      ? undefined
      : `all 230ms cubic-bezier(.21,1.02,.73,1)`,
    transform: `translateY(${offset * (top ? 1 : -1)}px)`,
    ...verticalStyle,
    ...horizontalStyle,
  }
}

const activeClass = css`
  z-index: 9999;
  > * {
    pointer-events: auto;
  }
`

const DEFAULT_OFFSET = 16

/** react-hot-toast Toaster와 동일하되, 호버 시 자동 닫힘 타이머를 멈추지 않음 */
export const NoPauseToaster: React.FC<ToasterProps> = ({
  reverseOrder,
  position = 'top-center',
  toastOptions,
  gutter,
  children,
  toasterId,
  containerStyle,
  containerClassName,
}) => {
  const { toasts, handlers } = useToaster(toastOptions, toasterId)

  return (
    <div
      data-rht-toaster={toasterId || ''}
      style={{
        position: 'fixed',
        zIndex: 9999,
        top: DEFAULT_OFFSET,
        left: DEFAULT_OFFSET,
        right: DEFAULT_OFFSET,
        bottom: DEFAULT_OFFSET,
        pointerEvents: 'none',
        ...containerStyle,
      }}
      className={containerClassName}
    >
      {toasts.map((t) => {
        const toastPosition = t.position || position
        const offset = handlers.calculateOffset(t, {
          reverseOrder,
          gutter,
          defaultPosition: position,
        })
        const positionStyle = getPositionStyle(toastPosition, offset)

        return (
          <ToastWrapper
            id={t.id}
            key={t.id}
            onHeightUpdate={handlers.updateHeight}
            className={t.visible ? activeClass : ''}
            style={positionStyle}
          >
            {t.type === 'custom' ? (
              resolveValue(t.message, t)
            ) : children ? (
              children(t)
            ) : (
              <ToastBar toast={t} position={toastPosition} />
            )}
          </ToastWrapper>
        )
      })}
    </div>
  )
}
